import { createStyles, Skeleton } from '@mantine/core';
import { IconArrowLeft, IconChevronDown, IconChevronUp, IconSearch } from '@tabler/icons-react';
import { useMemo, useState, type ReactNode } from 'react';

import HeaderBgr from '~/domains/common/components/AppShellHeader/components/Header/assets/HeaderBgr';
import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader/constants';
import Image from '~/domains/common/components/Image/Image';
import { UnstyledButtonLink } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';

const useStyles = createStyles((theme) => ({
  headerRoot: {
    position: 'sticky',
    top: 0,
    zIndex: theme.other.zIndex.header,
  },
  headerTopRow: {
    position: 'relative',
    zIndex: 1,
    height: HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `0 ${theme.other.spacing._16}px`,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.white,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  iconButtonPlaceholder: {
    width: 36,
    height: 36,
    flexShrink: 0,
  },
  titleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.other.spacing._8,
    height: 36,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 999,
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.white,
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0 8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  titleLabel: {
    color: theme.white,
    lineHeight: 1,
  },
  titleChevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.white,
  },
  menuRow: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    gap: theme.other.spacing._8,
    overflowX: 'auto',
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px ${theme.other.spacing._12}px`,
    '&::-webkit-scrollbar': { display: 'none' },
    scrollbarWidth: 'none',
  },
  menuItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.other.spacing._8,
    height: 36,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 999,
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.white,
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0 8px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
  leagueIcon: {
    width: 18,
    height: 18,
    minWidth: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  background: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
}));

export type LeaguePageHeaderItem = {
  id: string;
  label: string;
  iconUrl?: string;
  disabled?: boolean;
};

type LeaguePageHeaderProps = {
  backHref: string;
  items: LeaguePageHeaderItem[];
  activeItemId?: string;
  onSelectItem: (item: LeaguePageHeaderItem) => void;
  onSecondaryAction?: () => void;
  secondaryActionAriaLabel?: string;
  secondaryActionIcon?: ReactNode;
  variant?: 'quickPicks';
  isLoading?: boolean;
  renderItemIcon?: (item: LeaguePageHeaderItem, size: number) => ReactNode;
};

function DefaultItemIcon({ item, size }: { item: LeaguePageHeaderItem; size: number }) {
  if (!item.iconUrl) {
    return null;
  }

  return <Image src={item.iconUrl} alt={item.label} width={size} height={size} />;
}

export function LeaguePageHeader({
  backHref,
  items,
  activeItemId,
  onSelectItem,
  onSecondaryAction,
  secondaryActionAriaLabel = 'Search',
  secondaryActionIcon,
  variant,
  isLoading = false,
  renderItemIcon,
}: LeaguePageHeaderProps) {
  const { classes } = useStyles();
  const [expandedForId, setExpandedForId] = useState<string | null>(null);

  const activeItem = useMemo(
    () => items.find((item) => item.id === activeItemId) ?? items[0] ?? null,
    [activeItemId, items]
  );
  const otherItems = useMemo(
    () => items.filter((item) => item.id !== activeItem?.id),
    [activeItem?.id, items]
  );

  const isExpanded = expandedForId === activeItem?.id;

  const renderIcon = (item: LeaguePageHeaderItem) => {
    if (renderItemIcon) {
      return renderItemIcon(item, 18);
    }

    return <DefaultItemIcon item={item} size={18} />;
  };

  return (
    <div className={classes.headerRoot}>
      <div className={classes.background}>
        <HeaderBgr isQuickPicks={variant === 'quickPicks'} />
      </div>
      <div className={classes.headerTopRow}>
        <UnstyledButtonLink href={backHref} className={classes.iconButton}>
          <IconArrowLeft size={20} />
        </UnstyledButtonLink>

        {isLoading ? (
          <Skeleton height={36} width={120} /* eslint-disable-line local-rules/use-theme-tokens */ radius={999} />
        ) : (
          <button
            type="button"
            className={classes.titleButton}
            onClick={() => setExpandedForId(isExpanded ? null : (activeItem?.id ?? null))}
            disabled={!activeItem || otherItems.length === 0}
            aria-expanded={isExpanded}
          >
            {activeItem ? (
              <>
                <span className={classes.leagueIcon}>{renderIcon(activeItem)}</span>
                <Text variant="subhead-medium" className={classes.titleLabel}>
                  {activeItem.label}
                </Text>
                <span className={classes.titleChevron}>
                  {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </span>
              </>
            ) : null}
          </button>
        )}

        {onSecondaryAction ? (
          <button
            type="button"
            className={classes.iconButton}
            onClick={onSecondaryAction}
            aria-label={secondaryActionAriaLabel}
          >
            {secondaryActionIcon ?? <IconSearch size={20} />}
          </button>
        ) : (
          <span className={classes.iconButtonPlaceholder} />
        )}
      </div>

      {isExpanded ? (
        <div className={classes.menuRow}>
          {otherItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={classes.menuItem}
              onClick={() => onSelectItem(item)}
              disabled={item.disabled}
            >
              <span className={classes.leagueIcon}>{renderIcon(item)}</span>
              <Text variant="subhead-medium" c="white">
                {item.label}
              </Text>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
